<template>
  <div
    v-if="dropdownMenu"
    ref="root"
    class="admin-form-select-dropdown"
  >
    <button
      type="button"
      class="form-select admin-form-select admin-form-select-dropdown-toggle"
      :class="selectClass"
      :disabled="disabled"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="listbox"
      @click="toggleMenu"
    >
      <span class="admin-form-select-dropdown-text">{{ displayText }}</span>
    </button>
    <ul
      v-show="isOpen"
      class="admin-form-select-dropdown-menu"
      role="listbox"
      :style="{ maxHeight: menuMaxHeight }"
    >
      <li
        v-for="opt in normalizedOptions"
        :key="String(opt.value)"
        role="option"
        :aria-selected="isSelected(opt) ? 'true' : 'false'"
        class="admin-form-select-dropdown-item"
        :class="{ 'admin-form-select-dropdown-item--active': isSelected(opt) }"
        @click="selectOption(opt, $event)"
      >
        {{ opt.text }}
      </li>
    </ul>
  </div>
  <select
    v-else
    :value="selectValue"
    :disabled="disabled"
    class="form-select admin-form-select"
    :class="selectClass"
    @change="onChange"
  >
    <option v-if="placeholder" disabled value="">{{ placeholder }}</option>
    <option
      v-for="opt in normalizedOptions"
      :key="String(opt.value)"
      :value="opt.value === null || opt.value === undefined ? '' : String(opt.value)"
    >
      {{ opt.text }}
    </option>
  </select>
</template>

<script>
export default {
  name: 'AdminFormSelect',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    modelValue: {
      default: undefined
    },
    value: {
      default: undefined
    },
    options: {
      type: Array,
      default: () => []
    },
    disabled: Boolean,
    placeholder: {
      type: String,
      default: ''
    },
    selectClass: {
      type: [String, Array, Object],
      default: ''
    },
    dropdownMenu: {
      type: Boolean,
      default: false
    },
    visibleOptions: {
      type: Number,
      default: 4
    }
  },
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    currentValue() {
      const val = this.modelValue !== undefined ? this.modelValue : this.value;
      return val === null || val === undefined ? null : val;
    },
    selectValue() {
      return this.currentValue === null ? '' : String(this.currentValue);
    },
    normalizedOptions() {
      return (this.options || []).map(opt => {
        if (opt && typeof opt === 'object') {
          return {
            value: opt.value,
            text: (() => {
              if (opt.text !== null) return opt.text;
              if (opt.label !== null) return opt.label;
              return opt.value;
            })()
          };
        }
        return { value: opt, text: opt };
      });
    },
    displayText() {
      if (this.currentValue === null || this.currentValue === undefined || this.currentValue === '') {
        const placeholderOption = this.normalizedOptions.find(
          opt => opt.value === null || opt.value === undefined
        );
        return placeholderOption ? placeholderOption.text : this.placeholder || 'Select Filter';
      }
      const matched = this.normalizedOptions.find(opt => this.valuesMatch(opt.value, this.currentValue));
      return matched ? matched.text : String(this.currentValue);
    },
    menuMaxHeight() {
      const count = Number(this.visibleOptions) || 4;
      return `${count * 32}px`;
    }
  },
  mounted() {
    if (this.dropdownMenu) {
      this.onDocumentClick = event => {
        if (!this.isOpen) return;
        const { root } = this.$refs;
        if (root && !root.contains(event.target)) {
          this.isOpen = false;
        }
      };
      document.addEventListener('click', this.onDocumentClick);
    }
  },
  beforeDestroy() {
    if (this.onDocumentClick) {
      document.removeEventListener('click', this.onDocumentClick);
    }
  },
  methods: {
    valuesMatch(optionValue, currentValue) {
      if (optionValue === null || optionValue === undefined) {
        return currentValue === null || currentValue === undefined || currentValue === '';
      }
      return String(optionValue) === String(currentValue);
    },
    isSelected(opt) {
      return this.valuesMatch(opt.value, this.currentValue);
    },
    toggleMenu(event) {
      event.stopPropagation();
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
    },
    selectOption(opt, event) {
      event.stopPropagation();
      this.emitValue(opt.value === undefined ? null : opt.value);
      this.isOpen = false;
    },
    emitValue(value) {
      const normalized = value === undefined || value === '' ? null : value;
      this.$emit('update:modelValue', normalized);
      this.$emit('input', normalized);
      this.$emit('change', normalized);
    },
    onChange(event) {
      const raw = event.target.value;
      if (raw === '') {
        this.emitValue(null);
        return;
      }
      const matched = this.normalizedOptions.find(opt => String(opt.value) === raw);
      this.emitValue(matched ? matched.value : raw);
    }
  }
};
</script>

<style scoped>
.admin-form-select {
  height: 40px;
  border: 1px solid #e8edf4;
  border-radius: 10px;
  background-color: #fff;
  color: #1a2438;
  font-size: 14px;
  font-weight: 500;
  box-shadow: none;
}

.admin-form-select:focus {
  border-color: #c5d9f5;
  box-shadow: 0 0 0 3px rgba(13, 110, 212, 0.12);
}

.admin-form-select-dropdown {
  position: relative;
  width: 100%;
}

.admin-form-select-dropdown-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.admin-form-select-dropdown-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-form-select-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1050;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 4px 0;
  overflow-x: hidden;
  overflow-y: auto;
  list-style: none;
  border: 1px solid #f1f1f1;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.admin-form-select-dropdown-item {
  padding: 8px 12px;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  color: #555;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  cursor: pointer;
}

.admin-form-select-dropdown-item:hover,
.admin-form-select-dropdown-item--active {
  background: #e5efff;
  color: #000;
}

:is(.backend-content-dark, #app[data-bs-theme='dark']) .admin-form-select-dropdown-menu {
  border-color: #283548;
  background: #172033;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

:is(.backend-content-dark, #app[data-bs-theme='dark']) .admin-form-select-dropdown-item {
  color: #d5deeb;
}

:is(.backend-content-dark, #app[data-bs-theme='dark']) .admin-form-select-dropdown-item:hover,
:is(.backend-content-dark, #app[data-bs-theme='dark']) .admin-form-select-dropdown-item--active {
  background: #243049;
  color: #f5f7fb;
}
</style>
